/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {isNaN} from 'lodash';
import cloneDeep from 'lodash.clonedeep';
import invert from 'lodash.invert';
import isNull from 'lodash.isnull';
import isString from 'lodash.isstring';
import partition from 'lodash.partition';
import uniq from 'lodash.uniq';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {CheckboxState} from '@ion/core/src/types/general';
import {
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  alphaSortForObject
} from '@ion/core/src/utils/SortUtils';
import {
  contains
} from '@ion/core/src/utils/StringUtil';
import {DateSelectorValues, FilterItems, TrackingFilters} from '../types/types';

// definition return model of getMenuStyles...
export interface FilterMenuStyle {
  menuLeft: string | null;
  menuWidth: string | null;
  menuTop: string | null;
  contentGridTemplateColumns: string;
  contentJustifyContent: string;
  contentGridColumnGap: string | null;
  contentGridRowGap: string;
};

export interface FilterHashMapType {
  title: string;
  checked: CheckboxState;
  itemData: any;
  childrenIds: (number | string)[],
  parentId: number | string;
}

export interface FilterHashMapKeyType {
  [key: string]: FilterHashMapType
}

export interface CreateFilterHashMapParams {
  optionData: any[];
  optionPropId: string;
  optionPropLabel: string;
  optionPropGroupId?: string;
  optionPropGroupLabel?: string;
  optionPropGroupMembers?: string;
  optionPropGroupMember?: string;
  selectedOptionIds?: (number | string)[];
}

export interface getSavedFilterFormatParams {
  groupFilterState: FilterItems,
  assetFilterState: FilterItems,
  deviceFilterState: FilterItems,
  nodeKeys: {[key: string]: string}
}

/**
 * Takes a deeply nested item object and flattens it to a single
 * level object literal with each item's id as the key.
 *
 * ```typescript
 * import {createFilterHashMap} from "@ion/dls/src/components/filters/FilterHelpers";
 * const bloatedObject = [
 * ]
 *
 * const filterHashMap = createFilterHashMap(bloatedObject);
 * // {1234: {}, 4321: {}, ...}
 *
 * ```
 */
export const createFilterHashMap = (params: CreateFilterHashMapParams): FilterHashMapKeyType => {
  const {optionData}                        = params;
  const filterHashMap: FilterHashMapKeyType = {};
  optionData?.forEach((item: any) => {
    createFilterHashMapItem({item, parent: null}, params, filterHashMap);
    processChildren(item, params, filterHashMap);
  });
  return filterHashMap;
};

/**
 * @private
 */
const createFilterHashMapItem = (
  itemInfo: any,
  params: CreateFilterHashMapParams,
  hashMap: FilterHashMapKeyType
) => {
  const {item, parent} = itemInfo;
  const {
    optionPropLabel,
    optionPropGroupLabel,
    selectedOptionIds
  } = params;
  const itemId      = getId(item, params);
  const childrenIds = createChildrenIdList(item, params);
  const parentId    = getId(parent, params);
  const title       = item?.[optionPropGroupLabel] ?? item?.[optionPropLabel] ?? itemId;
  const checked     = selectedOptionIds?.includes?.(itemId) || selectedOptionIds?.includes?.(parentId)
    ? CheckboxState.Checked
    : CheckboxState.Unchecked;
  const itemData = createItemData(item, params);
  hashMap[itemId] = {
    title,
    checked,
    itemData,
    childrenIds,
    parentId
  };
  updateParentChecked(itemId, checked, hashMap);
};

/**
 * @private
 */
const processChildren = (
  parent: any,
  params: CreateFilterHashMapParams,
  hashMap: FilterHashMapKeyType
) => {
  const {optionPropGroupMembers} = params;
  if (parent?.[optionPropGroupMembers]) {
    parent[optionPropGroupMembers].forEach((item: any) => {
      createFilterHashMapItem({item, parent}, params, hashMap);
      processChildren(item, params, hashMap);
    });
  }
};

/**
 * @private
 */
const getId = (
  item: any,
  params: CreateFilterHashMapParams
) => {
  const {optionPropId, optionPropGroupId} = params;
  let id                                  = item?.[optionPropGroupId] ?? item?.[optionPropId] ?? null;
  if (isString(id)) {
    id = entityId(id) ?? id;
  }
  return id;
};

/**
 * @private
 */
const createItemData = (
  item: any,
  params: CreateFilterHashMapParams
) => {
  const {
    optionPropGroupId,
    optionPropGroupLabel,
    optionPropGroupMembers,
    optionPropGroupMember
  } = params;
  const itemData = cloneDeep(item);
  if (itemData) {
    delete itemData[optionPropGroupId];
    delete itemData[optionPropGroupLabel];
    delete itemData[optionPropGroupMembers];
    delete itemData[optionPropGroupMember];
  }
  return itemData && !isObjectEmpty(itemData) ? itemData : null;
};

/**
 * @private
 */
const createChildrenIdList = (
  item: any,
  params: CreateFilterHashMapParams
) => {
  const {optionPropGroupMembers, optionPropGroupMember} = params;
  const list = [];
  if (item?.[optionPropGroupMembers]) {
    item[optionPropGroupMembers].forEach?.((child: any) => {
      list.push(getId(child[optionPropGroupMember] ?? child, params));
    });
  }
  return list;
};

/**
 * Creates an ordered mapping of a filter hash map. The overall ordering is
 * alphabetical, and if an entry has children, children that are groups are
 * placed before children that are not groups.
 *
 * ```typescript
 * import {createFilterOrderedMap} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * createFilterOrderedMap(filterHashMap);
 *
 * ```
 */
export const createFilterOrderedMap = (hashMap: FilterHashMapKeyType) => {
  return createOrderedMap(alphaSortForObject(hashMap), null);
};

/**
 * @private
 */
const createOrderedMap = (map: [string, FilterHashMapType][], parentId: number | string) => {
  const group                 = map.filter(([, item]) => item.parentId === parentId);
  const [parents, nonParents] = partition(group, ([, item]) => item.childrenIds.length);
  let orderedMap = [];
  parents?.forEach(item => {
    const [itemId] = item;
    orderedMap = orderedMap.concat([item]);
    orderedMap = orderedMap.concat(createOrderedMap(map, entityId(itemId) ?? itemId));
  });
  orderedMap = orderedMap.concat(nonParents);
  return orderedMap;
};

/**
 * Updates the specified filter hash map to its appropriate checked state
 * by first updating the specified checkbox item, then traversing the hash
 * map "tree" both "up" (parents) and "down" (children) and updating those
 * checkbox items.
 *
 * ```typescript
 * import {updateFilterHashMapChecked} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * updateFilterHashMapChecked('1234', CheckboxState.Checked, filterHashMap);
 *
 * ```
 */
export const updateFilterHashMapChecked = (
  itemId: number | string,
  checked: CheckboxState,
  hashMap: FilterHashMapKeyType
) => {
  const updatedHashMap = cloneDeep(hashMap);
  const item = updatedHashMap?.[itemId];
  if (item) {
    item.checked = checked;
    updateChildrenChecked(itemId, checked, updatedHashMap);
    updateParentChecked(itemId, checked, updatedHashMap);
  }
  return updatedHashMap;
};

/**
 * Updates (i.e., either selects or deselects) all checkbox items in the
 * specified filter hash map.
 *
 * ```typescript
 * import {updateFilterHashMapChecked} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * updateFilterHashMapChecked('1234', CheckboxState.Checked, filterHashMap);
 *
 * ```
 */
export const updateFilterHashMapCheckedAll = (
  checked: CheckboxState,
  hashMap: FilterHashMapKeyType
) => {
  const updatedHashMap = cloneDeep(hashMap);
  Object.entries(updatedHashMap).forEach(([, item]) => {
    item.checked = checked;
  });
  return updatedHashMap;
};

/**
 * @private
 */
const updateChildrenChecked = (
  itemId: number | string,
  checked: CheckboxState,
  hashMap: FilterHashMapKeyType
) => {
  const childrenIds = hashMap[itemId]?.childrenIds;
  if (childrenIds?.length) {
    childrenIds.forEach(childId => {
      hashMap[childId].checked = checked;
      updateChildrenChecked(childId, checked, hashMap);
    });
  }
};

/**
 * @private
 */
const updateParentChecked = (
  itemId: number | string,
  checked: CheckboxState,
  hashMap: FilterHashMapKeyType
) => {
  const parentId = hashMap[itemId]?.parentId;
  if (!isNull(parentId)) {
    const siblings = hashMap[parentId]?.childrenIds;
    if (siblings?.length) {
      const allChecked = siblings.every(siblingId => {
        return hashMap[siblingId]?.checked === CheckboxState.Checked;
      });
      hashMap[parentId].checked = allChecked
        ? CheckboxState.Checked
        : CheckboxState.Unchecked;
      updateParentChecked(parentId, checked, hashMap);
    }
  }
};

/**
 * Returns all identifiers in the specified ordered filter hash map whose
 * items belong to the specified group.
 *
 * ```typescript
 * import {getFilterContentOptionsViaGroup} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * const contentOptionIds = getFilterContentOptionsViaGroup(orderedMap, '1234');
 *
 * ```
 */
export const getFilterContentOptionsViaGroup = (
  orderedMap: [string, FilterHashMapType][],
  parentId: number | string
) => {
  const contentOptions = orderedMap.filter(([, item]) => {
    return item.parentId === parentId;
  });
  return contentOptions.map(([itemId]) => itemId);
};

/**
 * Returns all identifiers in the specified ordered filter hash map whose
 * items match the specified search string.
 *
 * ```typescript
 * import {getFilterContentOptionsViaSearch} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * const contentOptionIds = getFilterContentOptionsViaSearch(orderedMap, 'my option');
 *
 * ```
 */
export const getFilterContentOptionsViaSearch = (
  orderedMap: [string, FilterHashMapType][],
  searchValue: string
) => {
  const contentOptions = orderedMap.filter(([, item]) => {
    return contains(item.title, searchValue, false);
  });
  return contentOptions.map(([itemId]) => itemId);
};

/**
 * Returns all identifiers in the specified ordered filter hash map whose
 * items are currently checked.
 *
 * ```typescript
 * import {getFilterSelectedOptions} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * const selectedOptionIds = getFilterSelectedOptions(orderedMap);
 *
 * ```
 */
export const getFilterSelectedOptions = (
  orderedMap: [string, FilterHashMapType][]
) => {
  const selectedOptions = orderedMap.filter(([, item]) => {
    return item.checked === CheckboxState.Checked;
  });
  return selectedOptions.map(([itemId]) => itemId);
};

/**
 * Returns a count of all identifiers in the specified ordered filter hash map
 * whose items are currently checked. Items which are groups may optionally be
 * omitted from the returned count.
 *
 * ```typescript
 * import {getFilterSelectedOptionsCount} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * const selectedOptionsCount = getFilterSelectedOptionsCount(orderedMap);
 *
 * ```
 */
export const getFilterSelectedOptionsCount = (
  orderedMap: [string, FilterHashMapType][],
  includeGroups: boolean
) => {
  const selectedOptions = orderedMap.filter(([, item]) => {
    return item.checked === CheckboxState.Checked && (includeGroups || item.childrenIds.length === 0);
  });
  return selectedOptions.length;
};

/**
 * Returns all option data in the specified ordered filter hash map whose
 * items are currently checked.
 *
 * ```typescript
 * import {getFilterSelectedOptionData} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * const selectedOptionData = getFilterSelectedOptionData(orderedMap);
 *
 * ```
 */
export const getFilterSelectedOptionData = (
  orderedMap: [string, FilterHashMapType][]
) => {
  const selectedOptions = orderedMap.filter(([, item]) => {
    return item.checked === CheckboxState.Checked && item.itemData;
  });
  return selectedOptions.map(([, item]) => item.itemData);
};

/**
 * The helper function helps to create the options for the Filter component.
 * This can be used when the options are an array of strings. This will return
 * the array of objects after removing duplicates, removing null values and
 * sorting them.
 *
 * ```typescript
 *
 * import {createFilterOptions} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * const options = createFilterOptions(['Asset', 'Tool', 'Machine', 'Asset']);
 *
 * ```
 */
export const createFilterOptions = (
  options: string[],
  labelKey = 'label',
  valuekey = 'value'
) => {
  return uniq(options)?.filter(Boolean).sort().map(item => ({
    [labelKey] : item,
    [valuekey] : item
  })) || [];
};

/**
 * This function allows for retrieving the value of a specific filter based on
 * the filters type, either a string or an object.
 */
export const getFilterValue = item => item?.id || item;

/**
  * This function allows for retrieving the label to be displayed for a specific
  * filter based on the filters type, either a string or an object.
  */
export const getFilterLabel = item => item?.name || item?.assetName || item?.iOnEventName || item;

/**
  * This function condenses both getFilterValue and getFilterLabel functions and
  * returns an object containing the filter label and value.
  */
export const resolveFilter = (item: any): any => ({
  value : getFilterValue(item),
  label : getFilterLabel(item)
});

// function that returns the title for a save filter
export function getFilterName(selectedFilterName?: string, savedFilterRecords?: object): string {
  if (savedFilterRecords) {
    // Returns 'Select Filter', if Saved filters exist but none applied.
    return translate('nav:filters.collapsed.selectFilter');
  } else if (selectedFilterName) {
    // Returns the applied saved filter name, if any.
    return selectedFilterName;
  }
  // Returns 'Save Filter', if no saved filters exist.
  return translate('nav:filters.collapsed.saveFilter');
};

export const isDateFilterActive = (filterState: TrackingFilters) => (
  filterState?.date?.period !== DateSelectorValues.Live
);

export const isBoundaryActive = (filterState: TrackingFilters) => !!filterState?.boundary?.length;

// function that returns boolean value that indicates selection in filter state...
export const hasSelectionInFilters = (filterState: TrackingFilters) => (
  isBoundaryActive(filterState) ||
  isDateFilterActive(filterState) ||
  Object.values(filterState)?.some((filter) => !!filter.selected?.length)
);

/**
 * @private getMaxWidth is a utility function provides max width of the
 * filter drop-down menu
 */
const getMaxWidth = (): string => {
  const isTracking            = document.getElementById('trackingSearchWrapper');
  const FILTER_WRAPPER_WIDTH  = document.getElementById('filterWrapper')?.offsetWidth;
  let toSubtract = 0;
  if (isTracking) {
    toSubtract = document.getElementById('saveFilter')?.offsetWidth;
  }
  const FILTERS_WIDTH         = FILTER_WRAPPER_WIDTH - toSubtract;
  return isNaN(FILTERS_WIDTH) ? '99.5%' : `${FILTERS_WIDTH * 0.99}px`;
};

/**
 * @private getLeftShift is a utility function provides min left shift of the
 * filter drop-down menu
 */
const getLeftShift = (isMoreFilter = false): string => {
  const isTracking = document.getElementById('trackingSearchWrapper');
  if (isMoreFilter) {
    // offsetLeft for moreFilter...
    const FILTER_WRAPPER_WIDTH  = document.getElementById('filterWrapper')?.offsetWidth;
    const SAVE_FILTER_WIDTH     = isTracking ? document.getElementById('saveFilter')?.offsetWidth : 0;
    const MORE_FILTER_WIDTH     = document.getElementById('moreFiltersWrapper')?.offsetWidth;
    return `-${FILTER_WRAPPER_WIDTH- SAVE_FILTER_WIDTH - MORE_FILTER_WIDTH + 10}px`;
  }
  return isTracking ? '20.2%' : '16px';
};

/**
 * @private getTopShift is a utility function provides min top shift of the
 * filter drop-down menu
 */
const getTopShift = (): string => {
  const isTracking = document.getElementById('trackingSearchWrapper');
  return isTracking ? '0' : '85px';
};

// function that returns Filter component menu container style...
export const getMenuStyles = (isMobile: boolean, isMoreFilter = false): FilterMenuStyle => {
  return {
    menuLeft                   : isMobile ? null : getLeftShift(isMoreFilter),
    menuWidth                  : isMobile ? null : getMaxWidth(),
    menuTop                    : isMobile ? null : getTopShift(),
    contentGridTemplateColumns : isMobile ? '1r' : 'repeat(4, 1fr)',
    contentJustifyContent      : isMobile ? 'left' : 'center',
    contentGridColumnGap       : isMobile ? null : '10px',
    contentGridRowGap          : '14px'
  };
};

/**
 * Defines a utility function to return Groups, Assets, Devices based on the Tree format
 *
 * ```typescript
 *
 * import {getSavedFilterFormat} from "@ion/dls/src/components/filters/FilterHelpers";
 *
 * const items = getSavedFilterFormat({nodeKeys, groupFilterState, assetFilterState, deviceFilterState});
 *
 * ```
 */
export const getSavedFilterFormat = ({
  groupFilterState,
  assetFilterState,
  deviceFilterState,
  nodeKeys
}: getSavedFilterFormatParams) => {
  // Groups Saved Filters
  const groups = getGroupSavedFilter(groupFilterState, nodeKeys);
  // Assets Saved filters
  const assets = getSavedFilterItems(assetFilterState, 'assetName', true);
  // Devices Saved filters
  const devices = getSavedFilterItems(deviceFilterState, 'deviceName', false);
  return [...groups, ...assets, ...devices];
};

// Defines a utility to return groups items
const getGroupSavedFilter = (groupState: FilterItems, nodeKeyMap: {[key: string]: string}) => {
  const {selected}     = groupState || [] as any;
  const keyMapInverted = invert(nodeKeyMap);
  const list           = selected?.map(item => {
    return {
      ...item,
      key : keyMapInverted[parseInt(item?.id)]
    };
  });
  return list || [];
};

// Defines a utility to return Saved Filter items [Assets / Devices]
const getSavedFilterItems = (filterState: FilterItems, property: string, isAsset: boolean = true) => {
  const {selected} = filterState || [] as any;
  const list       = selected?.map(item => {
    return {
      ...item,
      id       : parseInt(item?.accountId),
      assetId  : item?.id,
      deviceId : item?.id,
      title    : item?.name || item?.[property],
      isAsset  : isAsset,
      isDevice : !isAsset
    };
  });
  return list || [];
};
